package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceEventType
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.ParentNode

/**
 * 실시간 출결 피드(가이드 §3.4). 색상·아이콘은 C1 해소안을 단일 기준으로 쓴다 —
 * `AttendanceEvent.type`에서 결정론적으로 파생되므로 같은 라벨인데 아이콘이 달라지는 일이 없다.
 * - CHECK_IN  → success(초록) · icon-check-in
 * - CHECK_OUT → info(파랑)    · icon-check-out
 * - AMEND     → 원본 이벤트 취소선 처리(§3.4 C3, [amendedEventIds]로 표시)
 *
 * "이벤트 취소" 메뉴는 C3 해소안대로 삭제가 아니라 AMEND append다 — 이 패널은 취소 여부를
 * [amendedEventIds]로만 표시하고, 실제 취소 동작은 [wireWithin]의 콜백이 [AttendanceModal]에 위임한다.
 */
object AttendanceFeedPanel {
    private const val PANEL_ID = "feed-panel"

    fun render(events: List<AttendanceEvent>, amendedEventIds: Set<String>): String = """
        <aside class="eb-card eb-feed" id="$PANEL_ID" aria-labelledby="feed-title">
            ${innerHtml(events, amendedEventIds)}
        </aside>
    """.trimIndent()

    /** 피드만 다시 그릴 때(다음 flow 방출 시) 카드 안쪽만 교체한다 — 페이지 전체를 다시 그리지 않는다. */
    fun updateInPlace(events: List<AttendanceEvent>, amendedEventIds: Set<String>, onCancelRequested: (AttendanceEvent) -> Unit) {
        val panel = document.getElementById(PANEL_ID) as? HTMLElement ?: return
        panel.innerHTML = innerHtml(events, amendedEventIds)
        wireWithin(panel, events, onCancelRequested)
    }

    private fun innerHtml(events: List<AttendanceEvent>, amendedEventIds: Set<String>): String {
        if (events.isEmpty()) {
            return """
                <div class="eb-feed__header"><h2 id="feed-title">실시간 출결</h2></div>
                <p class="eb-meta">아직 오늘 출결 기록이 없어요.</p>
            """.trimIndent()
        }

        val items = events.sortedByDescending { it.occurredAt }.joinToString("") { event ->
            val cancelled = event.eventId in amendedEventIds
            val itemClass = if (cancelled) " eb-feed__item--cancelled" else ""
            val nameClass = if (cancelled) "eb-strike" else ""
            val time = event.occurredAt.substringAfter('T').take(5)
            val menu = if (event.type == AttendanceEventType.AMEND || cancelled) {
                "" // 이미 정정/취소된 이벤트, 또는 정정 이벤트 자신은 메뉴가 없다 — 다시 취소할 대상이 아니다.
            } else {
                """
                <button class="eb-icon-button eb-icon-button--sm eb-feed__menu-trigger" data-event-id="${event.eventId}" aria-haspopup="menu" aria-expanded="false" aria-label="${event.studentName.escapeHtml()} 학생 ${eventLabel(event)} 이벤트 메뉴">
                    ${icon("icon-more", "sm")}
                </button>
                <div class="eb-popover-menu" role="menu" hidden>
                    <button role="menuitem" class="eb-feed__view-detail" type="button">${icon("icon-search", "sm")}자세히 보기</button>
                    <button role="menuitem" class="eb-popover-menu__item--danger eb-feed__cancel" data-event-id="${event.eventId}" type="button">${icon("icon-close", "sm")}이벤트 취소</button>
                </div>
                """.trimIndent()
            }

            """
            <li class="eb-feed__item$itemClass">
                <time class="eb-feed__time">${time.escapeHtml()}</time>
                <span class="eb-feed__icon eb-feed__icon--${colorModifier(event.type)}">${icon(eventIcon(event.type), "sm")}</span>
                <div class="eb-feed__body">
                    <strong class="$nameClass">${event.studentName.escapeHtml()} 학생 ${eventLabel(event)}</strong>
                    <span class="eb-meta">${event.className?.escapeHtml() ?: "학원 전체"}${if (cancelled) " · <span class=\"eb-chip eb-chip--error eb-chip--xs\">취소됨</span>" else ""}</span>
                </div>
                $menu
            </li>
            """.trimIndent()
        }

        return """
            <div class="eb-feed__header">
                <h2 id="feed-title">실시간 출결</h2>
                <button class="eb-select" type="button" disabled>전체 ${icon("icon-chevron-down", "xs")}</button>
            </div>
            <ol class="eb-feed__list" aria-live="polite">$items</ol>
        """.trimIndent()
    }

    /** 최초 렌더 시(전체 페이지가 막 DOM에 붙었을 때) 호출. [updateInPlace]는 자체적으로 재배선한다. */
    fun wireWithin(root: ParentNode, events: List<AttendanceEvent>, onCancelRequested: (AttendanceEvent) -> Unit) {
        val triggers = root.querySelectorAll(".eb-feed__menu-trigger")
        for (index in 0 until triggers.length) {
            val trigger = triggers.item(index) as? HTMLElement ?: continue
            val menu = trigger.nextElementSibling as? HTMLElement ?: continue
            trigger.onclick = {
                val nowHidden = menu.hasAttribute("hidden")
                if (nowHidden) menu.removeAttribute("hidden") else menu.setAttribute("hidden", "")
                trigger.setAttribute("aria-expanded", nowHidden.toString())
                null
            }
        }

        val cancelButtons = root.querySelectorAll(".eb-feed__cancel")
        for (index in 0 until cancelButtons.length) {
            val button = cancelButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val eventId = button.getAttribute("data-event-id")
                events.firstOrNull { it.eventId == eventId }?.let(onCancelRequested)
                null
            }
        }
    }

    private fun eventLabel(event: AttendanceEvent): String = when (event.type) {
        AttendanceEventType.CHECK_IN -> "등원"
        AttendanceEventType.CHECK_OUT -> "하원"
        AttendanceEventType.AMEND -> "정정"
    }

    private fun eventIcon(type: AttendanceEventType): String = when (type) {
        AttendanceEventType.CHECK_IN -> "icon-check-in"
        AttendanceEventType.CHECK_OUT -> "icon-check-out"
        AttendanceEventType.AMEND -> "icon-edit"
    }

    private fun colorModifier(type: AttendanceEventType): String = when (type) {
        AttendanceEventType.CHECK_IN -> "success"
        AttendanceEventType.CHECK_OUT -> "info"
        AttendanceEventType.AMEND -> "error"
    }
}
